import SearchParamsBase from '../../../Elements/SearchParamsBase';
import ComparisonOperatorEnum from '../../../Enums/ComparisonOperatorEnum';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import PlanningStatusEnum from '../../../Enums/PlanningStatusEnum';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import selectHelper from '../../../Helpers/selectHelper';

export default class SLSearchParams extends SearchParamsBase {
  initialize() {
    this.ClientId = '';
    this.OpportunityId = '';
    this.ServiceId = '';
    this.OfficeIds = [];
    this.OwnerUserId = '';
    this.Status = [PlanningStatusEnum.Created, PlanningStatusEnum.ClientConfirmed, PlanningStatusEnum.OnField];
    this.EndDateTill = dateTimeHelper.indefiniteDateValue();
    this.Platform = [];
    this.ContractedVsPlannedVolumeOp = ComparisonOperatorEnum.Any;
    this.ContractedVsPlannedVolumeValue = '';
    this.PlannedVsActualVolumeOp = ComparisonOperatorEnum.Any;
    this.PlannedVsActualVolumeValue = '';
    this.ContractedVsPlannedRevenueOp = ComparisonOperatorEnum.Any;
    this.ContractedVsPlannedRevenueValue = '';
    this.PlannedVsActualRevenueOp = ComparisonOperatorEnum.Any;
    this.PlannedVsActualRevenueValue = '';
  }

  static defaultOfficeId = '';

  static ConvertToJSON(originalSearchItem) {
    const correctedSearchItem = { ...originalSearchItem };
    for (const prop in correctedSearchItem) {
      correctedSearchItem[prop] = correctedSearchItem[prop].toString();
    }
    return JSON.stringify(correctedSearchItem);
  }

  //-------------------------------------------
  //-------- Fields for Search Panel ----------
  //-------------------------------------------

  static tabsOnTheSearchPanel = [
    { label: 'General', id: 'general', icon: 'fa-solid fa-magnifying-glass' },
    { label: 'Variance', id: 'variance', icon: 'fa-solid fa-scale-unbalanced' },
  ];

  static fieldsInSearchBox = [
    { fieldName: 'ClientId', label: 'Client', className: 'col-6', type: InputTypesEnum.ClientSelect, tab: 'general' },
    {
      fieldName: 'OpportunityId',
      label: 'Opportunity',
      className: 'col-6',
      type: InputTypesEnum.OpportunitySelect,
      tab: 'general',
      idFilter: (searchItem) => searchItem.ClientId,
    },
    {
      fieldName: 'ServiceId',
      label: 'Service',
      className: 'col-6',
      type: InputTypesEnum.ServiceSelect,
      tab: 'general',
      idFilter: (searchItem) => searchItem.ClientId,
    },
    {
      fieldName: 'OfficeIds',
      label: 'Office',
      className: 'col-6',
      type: InputTypesEnum.OfficeOrGroupMultiSelect,
      tab: 'general',
    },
    { fieldName: 'OwnerUserId', label: 'Owner', className: 'col-6', type: InputTypesEnum.UserSelect, tab: 'general' },
    {
      fieldName: 'Status',
      label: 'Status',
      className: 'col-6',
      type: InputTypesEnum.MultiChoice,
      options: SLSearchParams.getStatusOptionsFunction(),
      tab: 'general',
    },
    {
      fieldName: 'EndDateTill',
      label: 'End Date Till',
      className: 'col-6',
      type: InputTypesEnum.DateWithIndefinite,
      tab: 'general',
    },
    {
      fieldName: 'Platform',
      label: 'Platform',
      className: 'col-6',
      type: InputTypesEnum.PlatformMultiChoice,
      tab: 'general',
    },
    {
      fieldName: 'ContractedVsPlannedVolumeOp',
      label: 'Planned − Contracted volume',
      className: 'col-6',
      type: InputTypesEnum.Select,
      options: SLSearchParams.getVarianceOperatorOptions(),
      tab: 'variance',
    },
    {
      fieldName: 'ContractedVsPlannedVolumeValue',
      label: 'Threshold',
      className: 'col-6',
      type: InputTypesEnum.Number,
      tab: 'variance',
      disabled: (item) => item.ContractedVsPlannedVolumeOp === ComparisonOperatorEnum.Any,
    },
    {
      fieldName: 'PlannedVsActualVolumeOp',
      label: 'Actual − Planned volume',
      className: 'col-6',
      type: InputTypesEnum.Select,
      options: SLSearchParams.getVarianceOperatorOptions(),
      tab: 'variance',
    },
    {
      fieldName: 'PlannedVsActualVolumeValue',
      label: 'Threshold',
      className: 'col-6',
      type: InputTypesEnum.Number,
      tab: 'variance',
      disabled: (item) => item.PlannedVsActualVolumeOp === ComparisonOperatorEnum.Any,
    },
    {
      fieldName: 'ContractedVsPlannedRevenueOp',
      label: 'Planned − Contracted revenue',
      className: 'col-6',
      type: InputTypesEnum.Select,
      options: SLSearchParams.getVarianceOperatorOptions(),
      tab: 'variance',
    },
    {
      fieldName: 'ContractedVsPlannedRevenueValue',
      label: 'Threshold',
      className: 'col-6',
      type: InputTypesEnum.NumberWithTwoDecimalPlaces,
      tab: 'variance',
      disabled: (item) => item.ContractedVsPlannedRevenueOp === ComparisonOperatorEnum.Any,
    },
    {
      fieldName: 'PlannedVsActualRevenueOp',
      label: 'Actual − Planned revenue',
      className: 'col-6',
      type: InputTypesEnum.Select,
      options: SLSearchParams.getVarianceOperatorOptions(),
      tab: 'variance',
    },
    {
      fieldName: 'PlannedVsActualRevenueValue',
      label: 'Threshold',
      className: 'col-6',
      type: InputTypesEnum.NumberWithTwoDecimalPlaces,
      tab: 'variance',
      disabled: (item) => item.PlannedVsActualRevenueOp === ComparisonOperatorEnum.Any,
    },
  ];

  static valuesToEraseOnChange = {
    values: [
      { triggerField: 'ClientId', toErase: 'OpportunityId', value: '' },
      { triggerField: 'ClientId', toErase: 'ServiceId', value: '' },
    ],
  };

  static iconName = 'fa-solid fa-search';
  static saveType = 'SLSearchParams';

  static getStatusOptionsFunction() {
    return selectHelper.generateSelectValues([
      'Created',
      'ClientConfirmed',
      'OnField',
      'Closed',
      'OnHold',
      'Cancelled',
    ]);
  }

  static getVarianceOperatorOptions() {
    return [
      { label: 'Any', value: ComparisonOperatorEnum.Any, disabled: false },
      { label: '>', value: ComparisonOperatorEnum.GreaterThan, disabled: false },
      { label: '≥', value: ComparisonOperatorEnum.GreaterThanOrEqual, disabled: false },
      { label: '<', value: ComparisonOperatorEnum.LessThan, disabled: false },
      { label: '≤', value: ComparisonOperatorEnum.LessThanOrEqual, disabled: false },
      { label: '=', value: ComparisonOperatorEnum.Equal, disabled: false },
      { label: '≠', value: ComparisonOperatorEnum.NotEqual, disabled: false },
    ];
  }
}
